'use client';

import { cva } from 'class-variance-authority';
import { Minus } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';

// =============================================================================
// Types
// =============================================================================

export type SelectionColor =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'info'
  | 'destructive';

export type SelectionSize = 'sm' | 'md' | 'lg';

export type SelectionVariant =
  | 'default'
  | 'card'
  | 'pill'
  | 'stacked'
  | 'icon-simple'
  | 'icon-card'
  | 'avatar'
  | 'payment'
  | 'simple'
  | 'chip';

export interface SelectionItem {
  /** Unique value for the option */
  value: string;
  /** Display label */
  label: string;
  /** Whether this option is disabled */
  disabled?: boolean;
  /** Secondary text (e.g., "$10/month") */
  subtext?: string;
  /** Supporting description text */
  description?: string;
  /** Leading icon component */
  icon?: React.ReactNode;
  /** Avatar element */
  avatar?: React.ReactNode;
  /** Image URL */
  image?: string;
  /** Alt text for the image (defaults to "" if omitted) */
  imageAlt?: string;
  /** Position of the image */
  imagePosition?: 'top' | 'left' | 'trailing';
  /** Badge element */
  badge?: React.ReactNode;
  /** Dot color for chip variant (named color or hex) */
  dotColor?: string;
  /** Price display (e.g., "$10") */
  price?: string;
  /** Price period (e.g., "per month") */
  pricePeriod?: string;
  /** Payment icon element */
  paymentIcon?: React.ReactNode;
  /** Action buttons */
  actions?: React.ReactNode;
}

// =============================================================================
// Hooks
// =============================================================================

interface UseSelectionStateOptions {
  /** Controlled value */
  value?: string;
  /** Default value for uncontrolled mode */
  defaultValue?: string;
  /** Callback when value changes */
  onValueChange?: (value: string) => void;
}

/**
 * Hook for managing single selection state (radio behavior).
 * Supports both controlled and uncontrolled modes.
 */
export function useSelectionState(
  options: UseSelectionStateOptions = {},
): [string | undefined, (value: string) => void] {
  const { value: controlledValue, defaultValue, onValueChange } = options;

  const [internalValue, setInternalValue] = React.useState<string | undefined>(
    defaultValue,
  );

  const isControlled = controlledValue !== undefined;
  const selectedValue = isControlled ? controlledValue : internalValue;

  const setValue = React.useCallback(
    (newValue: string) => {
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onValueChange?.(newValue);
    },
    [isControlled, onValueChange],
  );

  return [selectedValue, setValue];
}

interface UseMultiSelectionStateOptions {
  /** Controlled value */
  value?: string[];
  /** Default value for uncontrolled mode */
  defaultValue?: string[];
  /** Callback when value changes */
  onValueChange?: (value: string[]) => void;
}

/**
 * Hook for managing multi selection state (checkbox behavior).
 * Supports both controlled and uncontrolled modes.
 */
export function useMultiSelectionState(
  options: UseMultiSelectionStateOptions = {},
): [string[], (value: string, checked: boolean) => void] {
  const { value: controlledValue, defaultValue = [], onValueChange } = options;

  const [internalValue, setInternalValue] =
    React.useState<string[]>(defaultValue);

  const isControlled = controlledValue !== undefined;
  const selectedValues = isControlled ? controlledValue : internalValue;

  const setValue = React.useCallback(
    (itemValue: string, checked: boolean) => {
      const newValue = checked
        ? [...selectedValues, itemValue]
        : selectedValues.filter((v) => v !== itemValue);

      if (!isControlled) {
        setInternalValue(newValue);
      }
      onValueChange?.(newValue);
    },
    [isControlled, selectedValues, onValueChange],
  );

  return [selectedValues, setValue];
}

// =============================================================================
// Color Mappings
// =============================================================================

export const selectionColorClasses: Record<
  SelectionColor,
  {
    indicator: string;
    indicatorChecked: string;
    border: string;
    bg: string;
    text: string;
  }
> = {
  primary: {
    indicator: 'border-primary',
    indicatorChecked: 'bg-primary',
    border: 'border-primary',
    bg: 'bg-primary',
    text: 'text-primary-foreground',
  },
  secondary: {
    indicator: 'border-secondary',
    indicatorChecked: 'bg-secondary-foreground',
    border: 'border-secondary',
    bg: 'bg-secondary',
    text: 'text-secondary-foreground',
  },
  success: {
    indicator: 'border-success',
    indicatorChecked: 'bg-success',
    border: 'border-success',
    bg: 'bg-success',
    text: 'text-primary-foreground',
  },
  warning: {
    indicator: 'border-warning',
    indicatorChecked: 'bg-warning',
    border: 'border-warning',
    bg: 'bg-warning',
    text: 'text-primary-foreground',
  },
  info: {
    indicator: 'border-info',
    indicatorChecked: 'bg-info',
    border: 'border-info',
    bg: 'bg-info',
    text: 'text-primary-foreground',
  },
  destructive: {
    indicator: 'border-destructive',
    indicatorChecked: 'bg-destructive',
    border: 'border-destructive',
    bg: 'bg-destructive',
    text: 'text-primary-foreground',
  },
};

// =============================================================================
// CVA Variants
// =============================================================================

/**
 * Selection group container variants.
 * Used for the outer wrapper of a group of selection items.
 */
export const selectionGroupVariants = cva('flex', {
  variants: {
    orientation: {
      horizontal: 'flex-row flex-wrap',
      vertical: 'flex-col',
    },
    gap: {
      sm: 'gap-2',
      md: 'gap-3',
      lg: 'gap-4',
    },
  },
  defaultVariants: {
    orientation: 'horizontal',
    gap: 'md',
  },
});

/**
 * Selection indicator variants (for both radio and checkbox).
 * Renders as a rounded indicator with border.
 */
export const selectionIndicatorVariants = cva(
  'shrink-0 rounded-full border shadow-xs transition-[border-color,background-color,color,box-shadow] duration-(--duration-fast) ease-standard motion-reduce:transition-none flex items-center justify-center',
  {
    variants: {
      size: {
        sm: 'size-4',
        md: 'size-5',
        lg: 'size-6',
      },
    },
    defaultVariants: {
      size: 'sm',
    },
  },
);

/**
 * Checkbox indicator variants (square with rounded corners).
 */
export const checkboxIndicatorVariants = cva(
  'shrink-0 border shadow-xs transition-[border-color,background-color,color,box-shadow] duration-(--duration-fast) ease-standard motion-reduce:transition-none flex items-center justify-center',
  {
    variants: {
      size: {
        sm: 'size-4 rounded-[4px]',
        md: 'size-5 rounded-[5px]',
        lg: 'size-6 rounded-[6px]',
      },
    },
    defaultVariants: {
      size: 'sm',
    },
  },
);

/**
 * Inner dot sizes for radio indicators when checked.
 */
export const innerDotSizes: Record<SelectionSize, string> = {
  sm: 'size-2',
  md: 'size-3',
  lg: 'size-4',
};

/**
 * Text size classes for selection labels.
 */
export const textSizeClasses: Record<SelectionSize, string> = {
  sm: 'text-sm',
  md: 'text-sm',
  lg: 'text-sm',
};

/**
 * Icon size classes for checkbox indicators.
 */
export const iconSizeClasses: Record<SelectionSize, string> = {
  sm: 'size-3',
  md: 'size-3.5',
  lg: 'size-5',
};

// =============================================================================
// Draw-on checkmark SVG (shared delight animation)
// =============================================================================

export const CHECKMARK_PATH = 'M4 12l5 5L20 6';

export function DrawCheck({ className }: { className?: string }) {
  const svgRef = React.useRef<SVGSVGElement>(null);

  // A check drawn under reduced motion has a finished 0s animation. If the
  // preference flips back within the normal duration, the duration change would
  // restart it mid-draw. Cancelling the CSS animation on every preference change
  // (and on mount while reduced) makes it inert: base dashoffset 0 shows the full check.
  React.useEffect(() => {
    const svg = svgRef.current;
    const view = svg?.ownerDocument.defaultView;
    if (!svg || !view?.matchMedia) return;
    const query = view.matchMedia('(prefers-reduced-motion: reduce)');
    const cancelDraw = () => {
      for (const a of svg.getAnimations?.() ?? []) {
        if ((a as CSSAnimation).animationName === 'check-draw') a.cancel();
      }
    };
    if (query.matches) cancelDraw();
    if (query.addEventListener) {
      query.addEventListener('change', cancelDraw);
      return () => query.removeEventListener('change', cancelDraw);
    }
    // Legacy MediaQueryList (Safari < 14)
    query.addListener(cancelDraw);
    return () => query.removeListener(cancelDraw);
  }, []);

  return (
    <svg
      ref={svgRef}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn(
        className,
        // Constant animation-name: reduced motion only zeroes duration, so a
        // preference change never re-adds the animation and replays a finished check.
        '[stroke-dasharray:28] animate-[check-draw_var(--duration-fast)_var(--ease-standard)] motion-reduce:[animation-duration:0s]',
      )}
    >
      <path d={CHECKMARK_PATH} />
    </svg>
  );
}

DrawCheck.displayName = 'DrawCheck';

// =============================================================================
// SelectionIndicator Component
// =============================================================================

export interface SelectionIndicatorProps {
  /** Type of indicator to render */
  type: 'checkbox' | 'radio';
  /** Whether the indicator is checked */
  checked?: boolean;
  /** Whether the indicator is disabled */
  disabled?: boolean;
  /** Size of the indicator */
  size?: SelectionSize;
  /** Color of the indicator */
  color?: SelectionColor;
  /** Additional CSS classes */
  className?: string;
  /** Whether the checkbox is in indeterminate state (checkbox only) */
  indeterminate?: boolean;
}

/**
 * SelectionIndicator renders either a checkbox or radio indicator based on type.
 * For checkbox: renders a check icon when checked.
 * For radio: renders an inner dot when checked.
 */
const SelectionIndicator = React.forwardRef<
  HTMLSpanElement,
  SelectionIndicatorProps
>(
  (
    {
      type,
      checked = false,
      disabled = false,
      size = 'sm',
      color = 'primary',
      className,
      indeterminate = false,
    },
    ref,
  ) => {
    const colors = selectionColorClasses[color];

    if (type === 'checkbox') {
      return (
        <span
          ref={ref}
          data-slot="selection-indicator"
          data-type="checkbox"
          data-checked={checked}
          data-indeterminate={indeterminate || undefined}
          className={cn(
            checkboxIndicatorVariants({ size }),
            checked || indeterminate
              ? cn(
                  colors.indicatorChecked,
                  colors.border,
                  colors.text,
                  'border',
                )
              : 'border-input bg-background',
            disabled && 'opacity-50',
            className,
          )}
          aria-hidden="true"
        >
          {(checked || indeterminate) &&
            (indeterminate ? (
              <Minus className={iconSizeClasses[size]} strokeWidth={3} />
            ) : (
              <DrawCheck className={iconSizeClasses[size]} />
            ))}
        </span>
      );
    }

    // Radio type
    return (
      <span
        ref={ref}
        data-slot="selection-indicator"
        data-type="radio"
        data-checked={checked}
        className={cn(
          selectionIndicatorVariants({ size }),
          'transition-[background-color,border-color] duration-(--duration-fast) ease-standard motion-reduce:transition-none',
          'border-input bg-transparent',
          disabled && 'opacity-50',
          className,
        )}
        aria-hidden="true"
      >
        <span
          className={cn(
            'rounded-full transition-transform duration-(--duration-fast) ease-standard motion-reduce:transition-none',
            colors.indicatorChecked,
            innerDotSizes[size],
            checked ? 'scale-100' : 'scale-0',
          )}
        />
      </span>
    );
  },
);

SelectionIndicator.displayName = 'SelectionIndicator';

// =============================================================================
// FeaturedIcon Component
// =============================================================================

export interface FeaturedIconProps {
  /** The icon content to display */
  children: React.ReactNode;
  /** Size of the featured icon container */
  size?: SelectionSize;
  /** Additional CSS classes */
  className?: string;
}

// Hoisted outside component to avoid recreation on every render
const featuredIconContainerSizes: Record<SelectionSize, string> = {
  sm: 'size-8',
  md: 'size-10',
  lg: 'size-12',
};

const featuredIconInnerSizes: Record<SelectionSize, string> = {
  sm: 'size-4',
  md: 'size-5',
  lg: 'size-6',
};

/**
 * FeaturedIcon is a container for icons with border and shadow.
 * Used in icon-simple and icon-card variants.
 */
const FeaturedIcon = React.forwardRef<HTMLDivElement, FeaturedIconProps>(
  ({ children, size = 'sm', className }, ref) => {
    const containerSize = featuredIconContainerSizes[size];
    const innerSize = featuredIconInnerSizes[size];

    return (
      <div
        ref={ref}
        data-slot="featured-icon"
        className={cn(
          'shrink-0 rounded-md border border-border bg-card/60',
          'flex items-center justify-center',
          'dark:border-border',
          containerSize,
          className,
        )}
        style={{
          boxShadow: 'var(--shadow-inset-ring)',
        }}
      >
        <div
          className={cn(
            'text-secondary-foreground [&>svg]:block [&>svg]:size-full',
            innerSize,
          )}
        >
          {children}
        </div>
      </div>
    );
  },
);

FeaturedIcon.displayName = 'FeaturedIcon';

// =============================================================================
// ColorDot Component
// =============================================================================

export type ColorDotColor =
  | 'gray'
  | 'orange'
  | 'red'
  | 'darkRed'
  | 'brand'
  | 'success'
  | 'warning'
  | 'error'
  | string; // Allow custom hex colors

const dotColorMap: Record<string, string> = {
  gray: 'bg-muted',
  orange: 'bg-warning',
  red: 'bg-destructive',
  darkRed: 'bg-destructive-hover',
  brand: 'bg-primary/10',
  success: 'bg-success/10',
  warning: 'bg-warning/10',
  error: 'bg-destructive/10',
};

const dotSizeMap: Record<SelectionSize, string> = {
  sm: 'size-[10px]',
  md: 'size-3',
  lg: 'size-3.5',
};

export interface ColorDotProps {
  /** Color of the dot (named color or hex) */
  color?: ColorDotColor;
  /** Size of the dot */
  size?: SelectionSize;
  /** Additional CSS classes */
  className?: string;
}

/**
 * ColorDot renders a colored dot for chip variant.
 * Supports named colors (gray, orange, red, etc.) and hex colors.
 */
const ColorDot = React.forwardRef<HTMLSpanElement, ColorDotProps>(
  ({ color, size = 'sm', className }, ref) => {
    if (!color) return null;

    const colorClass = dotColorMap[color];
    const isHexColor = color.startsWith('#');

    return (
      <span
        ref={ref}
        data-slot="color-dot"
        className={cn(
          'shrink-0 rounded-full',
          dotSizeMap[size],
          !isHexColor && colorClass,
          className,
        )}
        style={isHexColor ? { backgroundColor: color } : undefined}
        aria-hidden="true"
      />
    );
  },
);

ColorDot.displayName = 'ColorDot';

// =============================================================================
// Exports
// =============================================================================

export { ColorDot, FeaturedIcon, SelectionIndicator };
